import { z } from "zod";
import { FontStyleDto } from "./font.ts";

export const ElementSecondsTimingDto = z.object({
  type: z.literal("SECOND_TIMING"),
  seconds: z.number(),
});

export const ElementImageTimingDto = z.object({
  type: z.literal("IMAGE_TIMING"),
  imageIndex: z.number(),
  countFrom: z.enum(["FIRST", "LAST"]),
  seconds: z.number(),
});

export const ElementTimingDto = z.union([
  ElementSecondsTimingDto,
  ElementImageTimingDto,
]);

export const ElementAnimationDto = z.object({
  kind: z.enum(["FADE_IN", "FADE_OUT"]),
  direction: z.enum(["FROM_LEFT", "FROM_RIGHT", "FROM_TOP", "FROM_BOTTOM"]),
  duration: z.number(),
  easing: z.enum(["LINEAR", "EASE_IN", "EASE_OUT", "EASE_IN_OUT"]),
});

export const ElementTranstionDto = z.object({
  timing: ElementTimingDto.nullable(),
  animation: ElementAnimationDto.nullable(),
});

export const ElementSolidColorDto = z.object({
  type: z.literal("SOLID_COLOR"),
  format: z.literal("HEX"),
  color: z.string(),
  opacity: z.number().min(0).max(100).optional(),
});

export const ElementColorDto = ElementSolidColorDto;

export const ElementBorderDto = z.object({
  color: ElementColorDto,
  width: z.number(),
});

export const ElementShadowDto = z.object({
  color: ElementColorDto,
  opacity: z.number(),
  x: z.number(),
  y: z.number(),
  blur: z.number(),
});

export const ElementPositionDto = z.object({
  type: z.literal("ABSOLUTE"),
  x: z.number(),
  y: z.number(),
});

export const ElementSizeDto = z.object({
  type: z.literal("ABSOLUTE"),
  w: z.number(),
  h: z.number(),
  lockAspectRatio: z.boolean().optional(),
});

export const ElementTypographyDto = z.object({
  fontSize: z.number(),
  fontWeight: z.number(),
  // Should be ok to delete in the future.
  fontFamily: z.string(),
  fontExternalId: z.string().optional(),
  fontStyle: FontStyleDto.default("normal"),
});

export const TextElementDto = z.object({
  externalId: z.string(),
  type: z.literal("TEXT"),
  text: z.string(),
  position: ElementPositionDto,
  opacity: z.number(),
  color: ElementColorDto,
  typography: ElementTypographyDto,
  shadow: ElementShadowDto.nullable(),
  enterAt: ElementTranstionDto,
  exitAt: ElementTranstionDto,
  isHidden: z.boolean().optional(),
});

export const ImageElementDto = z.object({
  externalId: z.string(),
  assetMediaExternalId: z.string(),
  type: z.literal("IMAGE"),
  position: ElementPositionDto,
  size: ElementSizeDto,
  opacity: z.number(),
  cornerRadius: z.number(),
  border: ElementBorderDto.nullable(),
  shadow: ElementShadowDto.nullable(),
  enterAt: ElementTranstionDto,
  exitAt: ElementTranstionDto,
  isHidden: z.boolean().optional(),
});

export const VideoElementDto = z.object({
  externalId: z.string(),
  assetMediaExternalId: z.string(),
  type: z.literal("VIDEO"),
  position: ElementPositionDto,
  size: ElementSizeDto,
  opacity: z.number(),
  volume: z.number().optional(),
  cornerRadius: z.number(),
  border: ElementBorderDto.nullable(),
  shadow: ElementShadowDto.nullable(),
  enterAt: ElementTranstionDto,
  exitAt: ElementTranstionDto,
  isHidden: z.boolean().optional(),
});

export const RectangleElementDto = z.object({
  externalId: z.string(),
  type: z.literal("RECTANGLE"),
  position: ElementPositionDto,
  size: ElementSizeDto,
  backgroundColor: ElementColorDto,
  opacity: z.number(),
  cornerRadius: z.number(),
  border: ElementBorderDto.nullable(),
  shadow: ElementShadowDto.nullable(),
  enterAt: ElementTranstionDto,
  exitAt: ElementTranstionDto,
  isHidden: z.boolean().optional(),
});

export const ElementDto = z.union([
  TextElementDto,
  ImageElementDto,
  VideoElementDto,
  RectangleElementDto,
]);

export const SceneTypeDto = z.enum(["mainScene", "startScene", "endScene"]);

export const SceneTransitionDto = z.object({
  kind: z.enum(["FADE_IN", "FADE_OUT", "STACKED"]),
  direction: z.enum(["FROM_LEFT", "FROM_RIGHT", "FROM_TOP", "FROM_BOTTOM"]),
  duration: z.number(),
  easing: z.enum(["LINEAR", "EASE_IN", "EASE_OUT", "EASE_IN_OUT"]),
});

export const SceneSecondsDurationDto = z.object({
  type: z.literal("SECOND_TIMING"),
  seconds: z.number(),
});

export const StartSceneDto = z.object({
  duration: SceneSecondsDurationDto,
  elements: ElementDto.array(),
  exitTransition: SceneTransitionDto.nullable(),
});

export const EndSceneDto = z.object({
  duration: SceneSecondsDurationDto,
  elements: ElementDto.array(),
  entryTransition: SceneTransitionDto.nullable(),
});

export const OverlayTemplateContentDto = z.object({
  elements: z.record(
    z.string(), // aspect ratio
    z.array(ElementDto),
  ),
  startScene: z.record(
    z.string(), // aspect ratio
    StartSceneDto,
  ),
  endScene: z.record(
    z.string(), // aspect ratio
    EndSceneDto,
  ),
});

export const UpsertOverlayTemplateDto = z.object({
  name: z.string(),
  content: OverlayTemplateContentDto,
});

export const DeleteOverlayTemplateResponseDto = z.object({
  success: z.boolean(),
  externalId: z.string(),
});

export const OverlayTemplateDto = z.object({
  externalId: z.string(),
  createdAt: z.string(),
  name: z.string(),
  content: OverlayTemplateContentDto,
});

export type ElementSecondsTimingDto = z.infer<typeof ElementSecondsTimingDto>;
export type ElementImageTimingDto = z.infer<typeof ElementImageTimingDto>;
export type ElementTimingDto = z.infer<typeof ElementTimingDto>;
export type ElementAnimationDto = z.infer<typeof ElementAnimationDto>;
export type ElementTranstionDto = z.infer<typeof ElementTranstionDto>;
export type ElementSolidColorDto = z.infer<typeof ElementSolidColorDto>;
export type ElementColorDto = z.infer<typeof ElementColorDto>;
export type ElementBorderDto = z.infer<typeof ElementBorderDto>;
export type ElementShadowDto = z.infer<typeof ElementShadowDto>;
export type ElementPositionDto = z.infer<typeof ElementPositionDto>;
export type ElementSizeDto = z.infer<typeof ElementSizeDto>;
export type ElementTypographyDto = z.infer<typeof ElementTypographyDto>;
export type TextElementDto = z.infer<typeof TextElementDto>;
export type ImageElementDto = z.infer<typeof ImageElementDto>;
export type VideoElementDto = z.infer<typeof VideoElementDto>;
export type RectangleElementDto = z.infer<typeof RectangleElementDto>;
export type ElementDto = z.infer<typeof ElementDto>;
export type SceneTypeDto = z.infer<typeof SceneTypeDto>;
export type SceneTransitionDto = z.infer<typeof SceneTransitionDto>;
export type SceneSecondsDurationDto = z.infer<typeof SceneSecondsDurationDto>;
export type StartSceneDto = z.infer<typeof StartSceneDto>;
export type EndSceneDto = z.infer<typeof EndSceneDto>;
export type OverlayTemplateContentDto = z.infer<
  typeof OverlayTemplateContentDto
>;
export type UpsertOverlayTemplateDto = z.infer<typeof UpsertOverlayTemplateDto>;
export type DeleteOverlayTemplateResponseDto = z.infer<
  typeof DeleteOverlayTemplateResponseDto
>;
export type OverlayTemplateDto = z.infer<typeof OverlayTemplateDto>;
